import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@nishtech/Store/Nishtech/NishtechLinksSelectors";
import NishtechLink from "@nishtech/Widgets/Basic/NishtechLink";
import * as React from "react";
import { CSSProperties, FC } from "react";

const enum fields {
    className = "className",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",

    titleColor = "titleColor",
    titleFontSize = "titleFontSize",
    titleFontWeight = "titleFontWeight",
    titleFontFamily = "titleFontFamily",
    titleTextDecoration = "titleTextDecoration",

    linkColor = "linkColor",
    linkFontSize = "linkFontSize",
    linkFontWeight = "linkFontWeight",
    linkFontFamily = "linkFontFamily",
    linkTextDecoration = "linkTextDecoration",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[] | any;
        [fields.customCSS]: string;

        [fields.titleColor]?: string;
        [fields.titleFontSize]?: string;
        [fields.titleFontWeight]?: string;
        [fields.titleFontFamily]?: string;
        [fields.titleTextDecoration]?: string;

        [fields.linkColor]?: string;
        [fields.linkFontSize]?: string;
        [fields.linkFontWeight]?: string;
        [fields.linkFontFamily]?: string;
        [fields.linkTextDecoration]?: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
}

const normalizeListInput = <T,>(list?: T[] | T | any): T[] => {
    if (!list) {
        return [];
    }
    try {
        if (Array.isArray(list)) {
            return list;
        }
        if (typeof (list as any).toArray === "function") {
            const arr = (list as any).toArray();
            if (Array.isArray(arr)) {
                return arr;
            }
        }
    } catch {
        // ignore
    }
    return [list] as T[];
};

const makeStyle = (values: {
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    textDecoration?: string;
}): CSSProperties => {
    const s: CSSProperties = {};
    if (values.color) {
        s.color = values.color;
    }
    if (values.fontSize) {
        s.fontSize = values.fontSize;
    }
    if (values.fontWeight) {
        const n = Number(values.fontWeight);
        (s as any).fontWeight = Number.isNaN(n) ? values.fontWeight : n;
    }
    if (values.fontFamily) {
        s.fontFamily = values.fontFamily;
    }
    if (values.textDecoration) {
        s.textDecoration = values.textDecoration;
    }
    return s;
};

export const LinkList: FC<OwnProps> = ({ fields }) => {
    const fieldLinks: LinkModel[] = normalizeListInput<LinkModel>(fields.links);

    const selector = (o: LinkModel | any) =>
        o && (o as any).fields ? (o as any).fields.destination : (o as unknown as LinkFieldValue);

    const links = useGetLinks<LinkModel>(fieldLinks, selector);

    const titleLink = useGetLink(fields.titleLink);
    const className = `as-footer-nav ${fields.className ?? ""}`.trim();

    const titleStyle = makeStyle({
        color: fields.titleColor,
        fontSize: fields.titleFontSize,
        fontWeight: fields.titleFontWeight,
        fontFamily: fields.titleFontFamily,
        textDecoration: fields.titleTextDecoration,
    });

    const linkStyle = makeStyle({
        color: fields.linkColor,
        fontSize: fields.linkFontSize,
        fontWeight: fields.linkFontWeight,
        fontFamily: fields.linkFontFamily,
        textDecoration: fields.linkTextDecoration,
    });

    if (!Array.isArray(links)) {
        return (
            <StyledWrapper as="div" className={className}>
                <div>
                    <h4 style={titleStyle}>
                        {titleLink?.url ? (
                            <Link className="as-opener" href={titleLink.url} style={titleStyle}>
                                {fields.title || (titleLink?.title ?? "")}
                            </Link>
                        ) : (
                            <a href="#" className="as-opener" style={titleStyle}>
                                {fields.title || ""}
                            </a>
                        )}
                    </h4>
                    <ul className="as-nav-slide" />
                </div>
            </StyledWrapper>
        );
    }

    const backToTopClickHandler = () => {
        const pageHolder = document.querySelector(".page-holder") as HTMLElement | null;

        if (pageHolder) {
            pageHolder.scrollIntoView({ behavior: "auto", block: "start" });
        }
    };
    return (
        <StyledWrapper as="div" className={className}>
            <div>
                <h4 style={titleStyle}>
                    {titleLink?.url ? (
                        <Link className="as-opener" href={titleLink.url} style={titleStyle}>
                            {fields.title || (titleLink?.title ?? "")}
                        </Link>
                    ) : (
                        <a href="#" className="as-opener" style={titleStyle}>
                            {fields.title || ""}
                        </a>
                    )}
                </h4>

                <ul className="as-nav-slide">
                    {links.map((link, index) => {
                        const widgetItem = fieldLinks[index];
                        const widgetFields = widgetItem?.fields;
                        const titleText = widgetFields?.overriddenTitle ?? link.title;

                        return (
                            <li key={index}>
                                {link?.url && (
                                    <>
                                        {widgetFields?.titlePrefix ? (
                                            <span style={linkStyle}>{`${widgetFields.titlePrefix} `}</span>
                                        ) : null}
                                        <NishtechLink
                                            href={link.url}
                                            target={widgetFields?.openInNewWindow ? "_blank" : ""}
                                            onClick={backToTopClickHandler}
                                            style={linkStyle}
                                        >
                                            <span>{titleText}</span>
                                        </NishtechLink>
                                    </>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.className,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }
                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "titlePrefix",
                        displayName: "Title Prefix (Without Link)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },

            {
                name: fields.titleColor,
                displayName: "Title color (CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.titleFontSize,
                displayName: "Title font size",
                editorTemplate: "TextField",
                defaultValue: "23px",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.titleFontWeight,
                displayName: "Title font weight",
                editorTemplate: "TextField",
                defaultValue: "700",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.titleFontFamily,
                displayName: "Title font family",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.titleTextDecoration,
                displayName: "Title text decoration",
                editorTemplate: "TextField",
                defaultValue: "none",
                fieldType: "General",
                tab: advancedTab,
            },

            {
                name: fields.linkColor,
                displayName: "Link color (CSS color)",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.linkFontSize,
                displayName: "Link font size",
                editorTemplate: "TextField",
                defaultValue: "16px",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.linkFontWeight,
                displayName: "Link font weight",
                editorTemplate: "TextField",
                defaultValue: "400",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.linkFontFamily,
                displayName: "Link font family",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.linkTextDecoration,
                displayName: "Link text decoration",
                editorTemplate: "TextField",
                defaultValue: "none",
                fieldType: "General",
                tab: advancedTab,
            },

            {
                name: fields.customCSS,
                editorTemplate: "TextField",
                displayName: "Custom CSS class (optional)",
                defaultValue: "",
                fieldType: "General",
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
